// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { AnyAction, reducers } from "@insite/client-framework/Store/Reducers";
import { ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { Draft } from "immer";
import merge from "lodash/merge";

export interface XCheckoutStateType {
    poNumber?: string;
    requiresMtr?: boolean;
    notes?: string;
    isSubmitOrderButtonDisabled?: boolean;
    shouldDisplayRequestedDateField?: boolean;
    saveOneTimeAddress?: boolean;
}

export const initialCheckoutState: XCheckoutStateType = {
    poNumber: "",
    requiresMtr: true,
    notes: "",
    isSubmitOrderButtonDisabled: false,
    shouldDisplayRequestedDateField: false,
    saveOneTimeAddress: false,
};

const reducer = {
    // we need to call reset after submitting order
    "xCheckout/Reset": (draft: Draft<XCheckoutStateType>) => {
        draft["poNumber"] = "";
        draft["requiresMtr"] = true;
        draft["notes"] = "";
        draft["isSubmitOrderButtonDisabled"] = false;
        draft["shouldDisplayRequestedDateField"] = false;
        draft["saveOneTimeAddress"] = false;
    },
    "xCheckout/SetNotes": (draft: Draft<XCheckoutStateType>, actions: { notes: string }) => {
        draft.notes = actions.notes;
    },
    "xCheckout/SetPoNumber": (draft: Draft<XCheckoutStateType>, actions: { poNumber: string }) => {
        draft.poNumber = actions.poNumber;
    },
    "xCheckout/SetRequiresMtr": (draft: Draft<XCheckoutStateType>, actions: { requiresMtr: boolean }) => {
        draft.requiresMtr = actions.requiresMtr;
    },
    "xCheckout/SetIsSubmitOrderButtonDisabled": (
        draft: Draft<XCheckoutStateType>,
        actions: { isSubmitOrderButtonDisabled: boolean },
    ) => {
        draft.isSubmitOrderButtonDisabled = actions.isSubmitOrderButtonDisabled;
    },
    "xCheckout/SetShouldDisplayRequestedDateField": (
        draft: Draft<XCheckoutStateType>,
        actions: { shouldDisplayRequestedDateField: boolean },
    ) => {
        draft.shouldDisplayRequestedDateField = actions.shouldDisplayRequestedDateField;
    },
    "xCheckout/SetSaveOneTimeAddress": (
        draft: Draft<XCheckoutStateType>,
        actions: { saveOneTimeAddress: boolean },
    ) => {
        draft.saveOneTimeAddress = actions.saveOneTimeAddress;
    },
};

const xCheckout = createTypedReducerWithImmer(initialCheckoutState, reducer);

(reducers as any).xCheckout = xCheckout;

export type xCheckoutState = {
    xCheckout: XCheckoutStateType;
} & ApplicationState;

type xCheckoutReducers = {
    [key: string]: typeof xCheckout;
};

export type xCheckoutActions = Parameters<xCheckoutReducers[keyof xCheckoutReducers]>[1] | AnyAction;
